import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { App, Descriptions, Image, Button, Space, Spin, Row, Col, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { CONTRACTOR_VIEW } from '../../../graphql/queries/contractors';
import { CONTRACTOR_DELETE } from '../../../graphql/mutations/contractors';
import CardLayout from '../../../layouts/CardLayout';

const { Title } = Typography;

const ContractorView: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { loading, data, refetch } = useQuery(CONTRACTOR_VIEW, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    refetch();
    const handleFocus = () => refetch();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  const [deleteContractor, { loading: deleteLoading }] = useMutation(CONTRACTOR_DELETE, {
    onCompleted: () => {
      message.success('Contractor successfully deleted');
      navigate('/admin/contractors');
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const handleDelete = () => {
    modal.confirm({
      title: 'Delete Contractor',
      content: 'Are you sure you want to delete this contractor?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => deleteContractor({ variables: { id } }),
    });
  };

  if (loading) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100dvh' }}>
        <Spin size="large" />
      </Row>
    );
  }

  const contractor = data?.contractor;

  if (!contractor) {
    return null;
  }

  return (
    <CardLayout>
      <Row gutter={[16, 24]}>
        <Col xs={24}>
          <Space wrap>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/contractor/edit/${id}`)}>
              Edit
            </Button>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={deleteLoading}>
              Delete
            </Button>
          </Space>
        </Col>

        <Col xs={24}>
          <Descriptions title="Contractor Information" bordered column={1} layout="horizontal">
            <Descriptions.Item label="Company Name">{contractor.name}</Descriptions.Item>
            <Descriptions.Item label="CEO">{contractor.ceo}</Descriptions.Item>
            <Descriptions.Item label="Phone">{contractor.phone}</Descriptions.Item>
            <Descriptions.Item label="Email">{contractor.email}</Descriptions.Item>
            <Descriptions.Item label="Website">
              {contractor.webSite && (
                <a href={contractor.webSite} target="_blank" rel="noopener noreferrer">
                  {contractor.webSite}
                </a>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="About">{contractor.about}</Descriptions.Item>
          </Descriptions>
        </Col>

        {contractor.logo && (
          <Col xs={24} sm={12} md={8}>
            <Title level={5}>Logo</Title>
            <Image src={contractor.logo} alt="Logo" style={{ maxWidth: '100%' }} />
          </Col>
        )}

        {contractor.photos && contractor.photos.length > 0 && (
          <Col xs={24}>
            <Title level={5}>Photos</Title>
            <Image.PreviewGroup>
              <Row gutter={[8, 8]}>
                {contractor.photos.map((photo: string, index: number) => (
                  <Col key={index} xs={24} sm={12} md={8} lg={6}>
                    <Image
                      src={photo}
                      alt={`Photo ${index + 1}`}
                      style={{ width: '100%', objectFit: 'cover' }}
                    />
                  </Col>
                ))}
              </Row>
            </Image.PreviewGroup>
          </Col>
        )}
      </Row>
    </CardLayout>
  );
};

export default ContractorView;
